import { IconX } from '@tabler/icons-react'
import { KeyboardEvent, MouseEvent, MouseEventHandler, ReactNode, Ref } from 'react'

import { SizeOptions } from '../../types/types'
import { AstraIcon } from '../AstraIcon/AstraIcon'
import { AstraParagraph } from '../Typography/AstraParagraph/AstraParagraph'
import { astraTwMerge } from '../Utilities/astraTwMerge'
import { colorToRgba, isLightColor } from '../Utilities/colorHelpers'

type Variant = 'filled' | 'outlined' | 'muted'
export type ChipColor = 'default' | 'brand' | 'info' | 'success' | 'caution' | 'critical' | 'magic' | (string & {})
/**
 * @deprecated Interactivity is no longer a color. Use the matching color with `interactive`, e.g. `color="info" interactive`.
 */
export type InteractiveColor = 'default-interactive' | 'info-interactive'
type HorizontalAlignment = 'left' | 'center'

const legacyInteractiveColors: Record<InteractiveColor, ChipColor> = {
    'default-interactive': 'default',
    'info-interactive': 'info',
}

const isLegacyInteractiveColor = (color: string): color is InteractiveColor => color in legacyInteractiveColors

export type AstraChipProps = {
    /**
     * The variant to use
     */
    variant?: Variant
    /**
     * Color of the chip. Can be a predefined color (default, brand, info, success, caution, critical, magic), a hex value (e.g., '#FF5733'), or an RGB/RGBA value (e.g., 'rgb(255, 87, 51)' or 'rgba(255, 87, 51, 0.8)')
     */
    color?: ChipColor | InteractiveColor
    /**
     * If `true`, the chip gets hover affordances. Defaults to `true` when `onClick` is set.
     * Set it explicitly for chips whose click is handled by a parent, such as a hover card or popover trigger.
     */
    interactive?: boolean
    /**
     * The size of the button
     */
    size?: SizeOptions
    /**
     * The label to use
     */
    label?: string | number | null
    /**
     * Icon to display before the label or by itself
     */
    icon?: ReactNode
    /**
     * If the component has an icon only and no label, provide an accessible label for screen readers
     */
    'aria-label'?: string
    /**
     * If `true`, the component is disabled
     */
    disabled?: boolean
    /**
     * If `true`, the button will take up the full width of its container.
     */
    fullWidth?: boolean
    /**
     * If `true`, the label will be truncated to the containers width.
     */
    truncated?: boolean
    /**
     * Icon to render after the label, if any.
     *
     */
    endIcon?: ReactNode
    /**
     * data-testid for testing purposes
     */
    'data-test'?: string
    /**
     * Ref to the div element
     */
    ref?: Ref<HTMLDivElement>
} & (ClickableChipProps | RemovableChipProps)

// A chip is either clicked as a whole or carries a remove button; a button nested in a button is unreachable for
// screen readers, so the two are mutually exclusive.
type ClickableChipProps = {
    /**
     * Function to be called when the chip is clicked. A chip with an `onClick` is focusable and can be activated with Enter or Space.
     */
    onClick?: MouseEventHandler<HTMLDivElement>
    onRemove?: never
    removeLabel?: never
}

type RemovableChipProps = {
    onClick?: never
    /**
     * Renders a remove button after the label. The button is its own click target with its own hover and focus
     * states, so clicking the label never removes.
     */
    onRemove?: MouseEventHandler<HTMLButtonElement>
    /**
     * Accessible label for the remove button. Defaults to "Fjern {label}".
     */
    removeLabel?: string
}

// Only colors transition: transition-all would animate the focus ring's width and offset, sweeping it outwards
const baseStyles =
    'truncate group border bg-surface-base border-transparent flex items-center px-1.5 py-2 rounded-md gap-1 overflow-hidden text-ellipsis transition-colors select-none disabled:cursor-not-allowed disabled:bg-surface-disabled'

// Without an outline-style the browser draws its own focus ring, which ignores both width and offset
const clickableStyles = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2'

// outline-offset-2 sets the ring apart from the chip; matching the ring width keeps the gap legible at 1x
const ringStyles: Record<ChipColor, string> = {
    default: 'outline-ring',
    brand: 'outline-brand-ring',
    info: 'outline-info-ring',
    critical: 'outline-critical-ring',
    success: 'outline-success-ring',
    caution: 'outline-caution-ring',
    magic: 'outline-magic-ring',
}

const disabledStyles = 'bg-surface-disabled border-disabled'

const outlinedColorStyles: Record<ChipColor, string> = {
    default: 'border border-base ',
    brand: 'border-brand-base border',
    info: 'border-info-base border',
    critical: 'border-critical-base border',
    success: 'border-success-base border',
    caution: 'border-caution-base border',
    magic: 'border-magic-base border',
}

const filledColorStyles: Record<ChipColor, string> = {
    default: 'bg-neutral-rest ',
    brand: 'bg-brand-rest',
    info: 'bg-info-rest',
    critical: 'bg-critical-rest ',
    success: 'bg-success-rest ',
    caution: 'bg-caution-rest ',
    magic: 'bg-magic-rest ',
}

const mutedColorStyles: Record<ChipColor, string> = {
    default: 'bg-transparent text-muted border border-subtle',
    brand: 'bg-transparent text-muted border border-subtle',
    info: 'bg-transparent text-muted border border-subtle',
    critical: 'bg-transparent text-muted border border-subtle',
    success: 'bg-transparent text-muted border border-subtle',
    caution: 'bg-transparent text-muted border border-subtle',
    magic: 'bg-transparent text-muted border border-subtle',
}

const colorStylesByVariant: Record<Variant, Record<ChipColor, string>> = {
    filled: filledColorStyles,
    outlined: outlinedColorStyles,
    muted: mutedColorStyles,
}

const filledHoverStyles: Record<ChipColor, string> = {
    default: 'hover:bg-neutral-hover ',
    brand: 'hover:bg-brand-hover ',
    info: 'hover:bg-info-hover ',
    critical: 'hover:bg-critical-hover ',
    success: 'hover:bg-success-hover ',
    caution: 'hover:bg-caution-hover ',
    magic: 'hover:bg-magic-hover ',
}

const outlinedHoverStyles: Record<ChipColor, string> = {
    default: 'hover:border-base hover:bg-neutral-hover',
    brand: 'hover:border-brand-base hover:bg-brand-hover',
    info: 'hover:border-info-base hover:bg-info-hover',
    critical: 'hover:border-critical-base hover:bg-critical-hover',
    success: 'hover:border-success-base hover:bg-success-hover',
    caution: 'hover:border-caution-base hover:bg-caution-hover',
    magic: 'hover:border-magic-base hover:bg-magic-hover',
}

const mutedHoverStyles: Record<ChipColor, string> = {
    default: 'hover:bg-neutral-subtle-hover',
    brand: 'hover:bg-neutral-subtle-hover',
    info: 'hover:bg-neutral-subtle-hover',
    critical: 'hover:bg-neutral-subtle-hover',
    success: 'hover:bg-neutral-subtle-hover',
    caution: 'hover:bg-neutral-subtle-hover',
    magic: 'hover:bg-neutral-subtle-hover',
}

const hoverStyles: Record<Variant, Record<ChipColor, string>> = {
    filled: filledHoverStyles,
    outlined: outlinedHoverStyles,
    muted: mutedHoverStyles,
}

// The remove button sits on the chip's own fill, so it hovers to the fill's hover token
const removeButtonHoverStyles: Record<Variant, Record<ChipColor, string>> = {
    filled: filledHoverStyles,
    outlined: filledHoverStyles,
    muted: mutedHoverStyles,
}

// An inset 1px outline draws the focus ring as a border without shifting the label
const removeButtonStyles =
    'flex shrink-0 cursor-pointer items-center justify-center rounded p-0.5 transition-colors focus-visible:outline focus-visible:outline-1 focus-visible:-outline-offset-1 disabled:cursor-not-allowed'

const horizontalAlignmentStyles: Record<HorizontalAlignment, string> = {
    left: 'justify-start',
    center: 'justify-center',
}

const textColorStyles: Record<ChipColor, string> = {
    default: 'text-default',
    brand: 'text-brand',
    info: 'text-info',
    critical: 'text-critical',
    success: 'text-success',
    caution: 'text-caution',
    magic: 'text-magic',
}

// Label and icon take their color from the chip, so a single class on the root drives both
const iconStyles = '[&>svg]:text-inherit group-aria-disabled:[&>svg]:text-disabled'

const labelStyles = 'text-nowrap text-inherit group-aria-disabled:text-disabled'

const getSizeStyles = (size: SizeOptions, hasLabel: boolean) => {
    const sizeStyles: Record<SizeOptions, string> = {
        small: `${hasLabel ? 'h-5 w-fit' : 'size-5'} `,
        medium: `${hasLabel ? 'h-6 w-fit' : 'size-6'} `,
        large: `${hasLabel ? 'h-7 w-fit' : 'size-7'} `,
    }
    return sizeStyles[size]
}

const getHorizontalAlignmentStyles = (hasLabel: boolean) => (hasLabel ? horizontalAlignmentStyles.left : horizontalAlignmentStyles.center)

const iconSizeStyles: Record<SizeOptions, string> = {
    small: '[&>svg]:size-3',
    medium: '[&>svg]:size-4',
    large: '[&>svg]:size-4',
}

const getCustomStyles = (isCustomFilled: boolean, isCustomOutlined: boolean, color: string): React.CSSProperties => {
    if (isCustomFilled) return { backgroundColor: color }
    if (isCustomOutlined) return { borderColor: color, '--chip-hover-bg': colorToRgba(color, 0.1) } as React.CSSProperties
    return {}
}

const getTextColorStyles = (isCustomColor: boolean, isCustomFilled: boolean, variant: Variant, predefinedColor: ChipColor, color: string): string => {
    // A custom fill can be any lightness, so the label has to flip rather than follow a semantic token
    if (isCustomFilled) return isLightColor(color) ? 'text-default' : 'text-inverted'
    if (isCustomColor) return 'text-default'
    // The muted variant carries text-muted with its background styles
    if (variant === 'muted') return ''
    return textColorStyles[predefinedColor] ?? ''
}

const getColorStyles = (
    disabled: boolean | undefined,
    isCustomColor: boolean,
    isCustomOutlined: boolean,
    variant: Variant,
    predefinedColor: ChipColor
): string => {
    if (disabled) return disabledStyles
    if (isCustomOutlined) return 'border'
    if (isCustomColor) return ''
    return colorStylesByVariant[variant][predefinedColor] ?? ''
}

export const AstraChip = ({
    ref,
    variant = 'outlined',
    size = 'medium',
    label,
    icon,
    disabled,
    fullWidth,
    truncated,
    color: colorProp = 'default',
    interactive,
    onClick,
    endIcon,
    onRemove,
    removeLabel,
    ...props
}: AstraChipProps) => {
    const color = isLegacyInteractiveColor(colorProp) ? legacyInteractiveColors[colorProp] : colorProp

    const hasLabel = label !== undefined && label !== null
    const isInteractive = !disabled && (interactive ?? (onClick !== undefined || isLegacyInteractiveColor(colorProp)))
    const isClickable = !disabled && onClick !== undefined

    // The chip is a div, so keyboard activation has to be wired up manually to match native button behaviour
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'Enter' && event.key !== ' ') return
        event.preventDefault()
        event.currentTarget.click()
    }

    // Removing unmounts the button, so taking focus on mousedown would drop it to the body
    const handleRemoveMouseDown = (event: MouseEvent<HTMLButtonElement>) => event.preventDefault()

    // Keeps the remove button's activation from bubbling into a parent that handles clicks or keys on the chip, such as a popover trigger or a select control
    const handleRemoveClick = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        onRemove?.(event)
    }

    const handleRemoveKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
        if (event.key === 'Enter' || event.key === ' ') event.stopPropagation()
    }

    // Check if color is a custom value (hex, RGB/RGBA, or oklch)
    const isCustomColor = color.startsWith('#') || color.startsWith('rgb') || color.startsWith('oklch')
    const predefinedColor = isCustomColor ? 'default' : color
    const isCustomOutlined = isCustomColor && variant === 'outlined'
    const isCustomFilled = isCustomColor && variant === 'filled'

    const textColorClass = getTextColorStyles(isCustomColor, isCustomFilled, variant, predefinedColor, color)

    const customStyles = getCustomStyles(isCustomFilled, isCustomOutlined, color)

    const colorStyles = getColorStyles(disabled, isCustomColor, isCustomOutlined, variant, predefinedColor)

    return (
        <div
            ref={ref}
            style={customStyles}
            className={astraTwMerge(
                `${baseStyles} ${getSizeStyles(size, hasLabel)} ${getHorizontalAlignmentStyles(hasLabel)}`,
                isInteractive || isCustomColor ? 'cursor-pointer' : 'cursor-default',
                isInteractive && !isCustomColor ? hoverStyles[variant][predefinedColor] : '',
                isCustomColor && !disabled ? 'hover:brightness-90' : '',
                isCustomOutlined && !disabled ? 'hover:bg-[var(--chip-hover-bg)]' : '',
                colorStyles,
                textColorClass,
                ringStyles[predefinedColor] ?? ringStyles.default,
                fullWidth ? 'w-full' : '',
                isClickable ? clickableStyles : '',
                onRemove ? `gap-0.5 ${size === 'large' ? 'pr-0.5' : 'pr-px'}` : ''
            )}
            aria-label={String(label)}
            aria-disabled={disabled}
            role={isClickable ? 'button' : undefined}
            tabIndex={isClickable ? 0 : undefined}
            onKeyDown={isClickable ? handleKeyDown : undefined}
            {...props}
            onClick={isClickable ? onClick : undefined}
        >
            {icon && <span className={`${iconStyles} ${iconSizeStyles[size]}`}>{icon}</span>}
            {label !== undefined && (
                <AstraParagraph
                    className={`${labelStyles} ${truncated ? 'truncate' : ''} ${onRemove ? 'pr-1' : ''}`}
                    variant={size === 'small' ? 'body-small' : 'body'}
                >
                    {String(label)}
                </AstraParagraph>
            )}
            {endIcon && <span className={`relative right-0 ${iconStyles} ${iconSizeStyles[size]}`}>{endIcon}</span>}
            {onRemove && (
                <button
                    type="button"
                    aria-label={removeLabel ?? `Fjern ${label ?? ''}`.trim()}
                    disabled={disabled}
                    onMouseDown={handleRemoveMouseDown}
                    onClick={handleRemoveClick}
                    onKeyDown={handleRemoveKeyDown}
                    className={astraTwMerge(
                        removeButtonStyles,
                        iconStyles,
                        iconSizeStyles[size],
                        ringStyles[predefinedColor] ?? ringStyles.default,
                        disabled ? '' : isCustomColor ? 'hover:bg-neutral-hover' : removeButtonHoverStyles[variant][predefinedColor]
                    )}
                >
                    <AstraIcon icon={IconX} size={size === 'small' ? 'small' : 'medium'} />
                </button>
            )}
        </div>
    )
}
